import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Text, View, StyleSheet, TouchableOpacity, Keyboard } from 'react-native';
import DraggableFlatList, { ScaleDecorator } from 'react-native-draggable-flatlist';
import { ScrollView } from 'react-native-gesture-handler';
import PropTypes from 'prop-types';
import { Background, ActivityIndicator } from 'LocalGluons';
import { useNavigation, useIsFocused } from '@react-navigation/native';
import { t } from 'Locale';
import CancelableTextInput from 'LocalGluons/components/CancelableTextInput';
import { Device } from 'DeviceUtils';
import favoritesInputProps from 'Pages/FavoriteSettings/utils/favoritesInputProps';
import { FavoritePageWrapper } from '../FavoritePageWrapper';
import { ChannelOrderListItem } from '../../components/ChannelOrderListItem';
import { ChannelOrderDialog } from '../../components/ChannelOrderDialog';
import favoritesSettingsStyles from '../../FavoritesSettings.jss';
import { moveElementInArray } from '../../utils';
import { useFavoriteSettingsContext } from '../../FavoriteSettingsContext';

const ChannelOrder = ({ screenProps: { onSaveCallback, onCancelCallback } }) => {
    const navigation = useNavigation();
    const { orderedFavoriteList, setOrderedFavoriteList, channelMap } =
        useFavoriteSettingsContext();

    // Intentionally duplicated because of android bug
    // when text gets duplicated when edited outside of input
    // remove when react-native fixes this
    const [query, setQuery] = useState('');

    const [changeOrderItem, setChangeOrderItem] = useState(null);
    const navWillBlurListener = useRef({});

    // TODO: REFACTOR: DUPLICATE WITH MANAGEFAVORITES.JS https://jira.24i.com/browse/PRJ1260EXT-3327
    const onChangeSearchTextCallback = useCallback((text) => {
        setQuery(text.toLowerCase());
    }, []);

    const channels = useMemo(() => {
        if (!orderedFavoriteList || orderedFavoriteList.length === 0) {
            return [];
        }
        const returnChannels = orderedFavoriteList.reduce((newChannels, channelId) => {
            const channel = channelMap[channelId];
            if (!channel)
                console.warn(`Channel ${channelId} not in channel map!`, { channelId, channelMap });
            if ((channel && !query) || (channel && channel.name.toLowerCase().startsWith(query))) {
                newChannels.push({
                    ...channel,
                    index: query ? orderedFavoriteList.indexOf(channelId) : newChannels.length,
                    channelId
                });
            }
            return newChannels;
        }, []);
        return returnChannels;
    }, [orderedFavoriteList, channelMap, query]);

    const channelOrderDialogCallback = useCallback(
        (newPosition) => {
            if (changeOrderItem) {
                const newIndex = parseInt(newPosition - 1, 10);
                const { index } = changeOrderItem.item;

                const newOrderedFavoriteList = moveElementInArray(
                    orderedFavoriteList,
                    index,
                    newIndex
                );

                setOrderedFavoriteList(newOrderedFavoriteList);

                setChangeOrderItem(null);
            }
        },
        [changeOrderItem, orderedFavoriteList, setOrderedFavoriteList]
    );

    useEffect(() => {
        navWillBlurListener.current = navigation.addListener('blur', () => {
            Keyboard.dismiss();
        });

        return () => {
            navWillBlurListener?.current();
        };
    }, [navigation]);

    const isFocused = useIsFocused();

    if (!isFocused) {
        return <ActivityIndicator />;
    }

    const renderItem = (itemData) => (
        <ScaleDecorator>
            <ChannelOrderListItem
                {...itemData}
                key={`draggable-channel-${itemData.channelId}`}
                testID={`channel_${itemData.channelId}`}
                disableMove={Boolean(query)}
                onNumberPress={() => setChangeOrderItem(itemData)}
                isLast={itemData.getIndex() === channels.length - 1}
            />
        </ScaleDecorator>
    );

    return (
        <Background style={{ flex: 1 }}>
            {changeOrderItem && (
                <ChannelOrderDialog
                    itemTitle={changeOrderItem.item.name}
                    currentPosition={changeOrderItem.item.index + 1}
                    onCancel={() => setChangeOrderItem(null)}
                    onConfirm={channelOrderDialogCallback}
                />
            )}
            <FavoritePageWrapper
                onSaveCallback={onSaveCallback}
                onCancelCallback={onCancelCallback}
            >
                <View style={favoritesSettingsStyles.searchMarginContainer}>
                    <CancelableTextInput
                        {...{
                            ...favoritesInputProps,
                            containerStyle: Device.isWeb
                                ? {}
                                : favoritesSettingsStyles.searchInputContainer,
                            placeholder: t('SETTINGS.FAVOURITE_CHANNELS_SEARCH'),
                            onChangeText: onChangeSearchTextCallback,
                            displayCancelIcon: Boolean(query),
                            onCancel: (inputRef) => {
                                setQuery('');
                                inputRef.clear();
                            }
                        }}
                    />
                </View>
                <ScrollView>
                    <DraggableFlatList
                        data={channels}
                        onDragEnd={({ from, to }) => {
                            const newOrderedFavoriteList = moveElementInArray(
                                orderedFavoriteList,
                                from,
                                to
                            );
                            setOrderedFavoriteList(newOrderedFavoriteList);
                        }}
                        keyExtractor={(item) => item.channelId}
                        renderItem={renderItem}
                    />
                </ScrollView>
            </FavoritePageWrapper>
        </Background>
    );
};

ChannelOrder.propTypes = {
    screenProps: PropTypes.shape({
        onSaveCallback: PropTypes.func,
        onCancelCallback: PropTypes.func
    }).isRequired
};

export { ChannelOrder };
